import React, { forwardRef, useEffect, useState, useRef } from 'react';
import { useSelector } from 'react-redux';
import { Link, useHistory, useParams } from 'react-router-dom';
import { Helmet } from "react-helmet";
import { toast } from "react-toastify";
import ApplicationLayout from '../../../layouts/ApplicationLayout';
import PageHeader from '../../includes/PageHeader';
import TapIcon from '../../../services/TapIcon';
import { ORG_AUTH_URL } from '../../../services/TapApiUrls';
import HttpAPICall from '../../../services/HttpAPICall';
import Loader from '../../../components/ui/Loader/Loader';
import TapHelper from '../../../services/TapHelper';
import DateService from '../../../services/DateService';
import { DisplayListPagination } from '../../../components/TapUi';
import Status from '../../../components/ui/Status';
import PassCodeDetail from './PassCodeDetail';
import swal from "sweetalert";

const PassCodeList = forwardRef((props, ref) => {

    let initListData = {
        loading: false,
        minimizeTable: false,
        items: [],
        meta: null,
        page: 1
    };

    let initVoucherData = {
        id: null,
        loading: false,
        data: null,
        passcode: null
    };

    let itemListUrl = ORG_AUTH_URL + "/passcode/list";
    let voucherDetailUrl = ORG_AUTH_URL + "/passcode/detail";

    const access_token = useSelector(state => state.auth.access_token);
    const history = useHistory();
    const { id } = useParams();

    const [itemsData, setItemsData] = useState({ ...initListData });
    const [voucherData, setVoucherData] = useState({ ...initVoucherData });

    const isDetailLoading = useRef(false);

    const loadItemsData = (page = 1) => {
        setItemsData(pd => ({ ...pd, loading: true, page: page }));
        let params = { page: page };

        HttpAPICall.withAthorization('GET', itemListUrl, access_token, params, {}, (response) => {
            let respData = response.data;
            let meta = TapHelper.pick(respData, ['from', 'to', 'total', 'last_page', 'per_page', 'current_page']);
            setItemsData(pd => ({ ...pd, items: respData.data, meta: meta, loading: false }));
        }).catch(() => {
            setItemsData(pd => ({ ...pd, loading: false }));
        });
    }

    const loadVoucherDetail = (id) => {
        if (isDetailLoading.current) return;

        isDetailLoading.current = true;
        setVoucherData(pd => ({ ...pd, loading: true, id, data: null, passcode: null }));

        HttpAPICall.withAthorization('GET', voucherDetailUrl + "/" + id, access_token, {}, {}, (response) => {
            setVoucherData(pd => ({
                ...pd,
                data: response.data,
                passcode: response.data?.passcode || null,
                loading: false
            }));
            isDetailLoading.current = false;
        }).catch(() => {
            setVoucherData(pd => ({ ...pd, loading: false, data: null, passcode: null }));
            isDetailLoading.current = false;
            toast.error("Failed to load passcode details");
        });
    }

    const viewVoucherDetail = (id) => {
        history.push(`/passcode/${id}`);
    };
    const closeVoucherDetail = () => {
        isDetailLoading.current = false;
        setVoucherData({ ...initVoucherData });
        setItemsData(pd => ({ ...pd, minimizeTable: false }));
        history.push("/passcode");
    }

    const handlerVoucherAdd = () => {
        history.push("/passcode_add");
    }

    const handlerVoucherEdit = () => {
        if (!voucherData.id) return;
        history.push({
            pathname: `/passcode_edit/${voucherData.id}`,
            state: { editData: voucherData.data }
        });
    }

    const handleDelete = () => {
        swal({
            title: "Delete",
            text: "Are you sure want to delete this passcode permanently?",
            icon: "warning",
            buttons: ["No", "Yes"],
        }).then(willDelete => {
            if (willDelete) {
                HttpAPICall.withAthorization('DELETE', ORG_AUTH_URL + '/passcode/delete/' + voucherData?.id, access_token, {}, {}, (resp) => {
                    toast.success(resp.data.msg);
                    closeVoucherDetail();
                    loadItemsData(1);
                });
            }
        });
    }

    useEffect(() => {
        loadItemsData(1);
    }, []);

    useEffect(() => {
        if (id) {
            setItemsData(prev => ({
                ...prev,
                minimizeTable: true
            }));

            loadVoucherDetail(id);
        } else {
            isDetailLoading.current = false;

            setItemsData(prev => ({
                ...prev,
                minimizeTable: false
            }));

            setVoucherData(initVoucherData);
        }
    }, [id]);

    const voucherDetailJsx = () => {
        return voucherData.loading
            ? <div className='text-center'><Loader /></div>
            : <PassCodeDetail data={voucherData.data} loading={voucherData.loading} />
    }

    const voucherListJsx = () => {
        return (
            <>
                <table className="table table-sm table-bordered align-middle bg-white">
                    <thead className="align-middle table-secondary">
                        {itemsData.minimizeTable ?
                            <tr className='text-center'>
                                <th scope="col">PassCode</th>
                            </tr> :
                            <tr className='text-center'>
                                <th style={{ width: "5%" }}>S.No</th>
                                <th style={{ width: "15%" }}>Passcode</th>
                                <th style={{ width: "12%" }}>Status</th>
                                <th style={{ width: "13%" }}>Expiry Date</th>
                                <th style={{ width: "16%" }}>Visibility</th>
                                <th style={{ width: "13%" }}>Created By</th>
                                <th style={{ width: "17%" }}>Sites</th>
                                <th style={{ width: "5%" }}>Action</th>
                            </tr>
                        }
                    </thead>
                    <tbody>
                        {itemsData.loading ? (
                            <tr><td className='text-center' colSpan={9}><Loader /></td></tr>
                        ) : itemsData.items?.length > 0 ? (
                            itemsData.items.map((item, index) => (
                                <tr className={['text-center', voucherData.id == item.id ? 'bg-light' : ''].join(' ')} key={index}>
                                    {itemsData.minimizeTable ? (
                                        <td className='text-start'>
                                            <Link onClick={() => viewVoucherDetail(item.id)} className="text-decoration-none" role="button">
                                                {item.passcode}
                                            </Link>
                                            <div className='float-end text-xs'>{DateService.dateFormat(item.transaction_date)}</div>
                                            <div className="mt-1">
                                                <span className="text-sm">{DateService.dateFormat(item.expiry_date)}</span>
                                                <span className="float-end text-sm">
                                                    <Status color={item?.status?.status_color_code}>{item.status?.status_text}</Status>
                                                </span>
                                            </div>
                                        </td>
                                    ) : (
                                        <>
                                            <td>{itemsData.meta ? itemsData.meta.from + index : index + 1}</td>
                                            <td>
                                                <button
                                                    onClick={() => viewVoucherDetail(item.id)}
                                                    className="btn btn-link text-decoration-none"
                                                >
                                                    {item.passcode}
                                                </button>
                                            </td>
                                            <td><Status color={item?.status?.status_color_code}>{item.status?.status_text}</Status></td>
                                            <td>{DateService.dateFormat(item.expiry_date)}</td>
                                            <td>{item.visibility?.join(', ') || '-'}</td>
                                            <td>{item.created_by || '-'}</td>
                                            <td>
                                                {item.site_data?.length > 0 ? (
                                                    <>
                                                        {item.site_data.slice(0, 2).map(s => s.name).join(', ')}
                                                        {item.site_data.length > 2 && ` +${item.site_data.length - 2} more`}
                                                    </>
                                                ) : '-'}
                                            </td>
                                            <td>
                                                <div className="dropdown">
                                                    <button type="button" id={`actionMenuButton_${item.id}`} data-bs-toggle="dropdown" className="action-btn">
                                                        <TapIcon.FontAwesomeIcon icon={TapIcon.faEllipsisV} />
                                                    </button>
                                                    <ul className="dropdown-menu">
                                                        <li>
                                                            <button className="dropdown-item" onClick={() => viewVoucherDetail(item.id)}>
                                                                View
                                                            </button>
                                                        </li>
                                                    </ul>
                                                </div>
                                            </td>
                                        </>
                                    )}
                                </tr>
                            ))
                        ) : (
                            <tr><td className='text-center' colSpan={9}>No Record</td></tr>
                        )}
                    </tbody>
                </table>
                <DisplayListPagination meta={itemsData.meta} onPageChange={(e) => loadItemsData(e.selected + 1)} />
            </>
        );
    }

    const topRightHeaderJsx = () => {
        return !itemsData.minimizeTable && (
            <button type="button" className="btn btn-primary" disabled={itemsData.loading} onClick={handlerVoucherAdd}>
                Add
            </button>
        );
    }

    const pageHeading = () => {
        return itemsData.minimizeTable ? (
            <>
                <Helmet><title>PassCode Detail</title></Helmet>
                <div className="page_title mt-2 mx-0 row align-middle">
                    <div className='col-sm-12'>
                        <div className='row'>
                            <div className='col-sm-3'>
                                <h3 className='mb-0'>PassCode</h3>
                                <div className='text-end d-flex align-items-center justify-content-end mt15'>
                                    {topRightHeaderJsx()}
                                </div>
                            </div>
                            <div className='col-sm-9'>
                                <h3 className='mb-0'>PassCode : {voucherData.passcode || ''}</h3>
                                <div className='text-end d-flex align-items-center justify-content-end mt15'>
                                    <button disabled={voucherData.loading} type="button" id='actionMenuButton' data-bs-toggle="dropdown" className="btn btn-secondary">
                                        <TapIcon.imageIcon icon={TapIcon.ActionIcon} className="img-fluid" alt="item-action" />
                                    </button>
                                    <ul className="dropdown-menu">
                                        <li className='dropdown-item py-1'>
                                            <button className="btn btn-link text-start text-decoration-none w-100 text-dark mx-0" onClick={handlerVoucherEdit}>
                                                Edit
                                            </button>
                                        </li>
                                        <li className='dropdown-item py-1'>
                                            <button className="btn btn-link text-start text-decoration-none w-100 text-dark mx-0" onClick={handleDelete}>
                                                Delete
                                            </button>
                                        </li>
                                    </ul>
                                    <button type="button" className="btn btn-secondary" disabled={voucherData.loading} onClick={closeVoucherDetail}>
                                        <TapIcon.imageIcon icon={TapIcon.CloseIcon} />
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </>
        ) : (
            <PageHeader title="PassCode - List" name="PassCode" rightTopHeadingJsx={topRightHeaderJsx()} />
        );
    }

    return (
        <ApplicationLayout>
            {pageHeading()}
            <div className="container-fluid pl5">
                <div className='page_containt row'>
                    <div className={['pageTbl', itemsData.minimizeTable ? 'col-sm-3' : 'col-sm-12'].join(' ')}>
                        {voucherListJsx()}
                    </div>
                    {itemsData.minimizeTable && (
                        <div className="tap_border_left col-sm-9 p-2 pageTbl bg-white">
                            {voucherDetailJsx()}
                        </div>
                    )}
                </div>
            </div>
        </ApplicationLayout>
    );
});

export default PassCodeList;